import React from "react";
import { Alert, Card, Col, InputNumber, Radio, Row, Space, Statistic, Typography } from "antd";
import dayjs from "dayjs";
import { Sex, SoberInfo } from "./types";
import { useTranslation } from "react-i18next";
import { IMPLAUSIBLE_PROMILLE_THRESHOLD } from "./constants";

const { Text } = Typography;

type UserMetabolismCardProps = {
    weightKg: number;
    sex: Sex;
    onWeightChange: (value: number) => void;
    onSexChange: (value: Sex) => void;
    isSelectedDayToday: boolean;
    latestPromille: number;
    rawPromille: number;
    peakPromille: number;
    breathMgL: number;
    soberInfo: SoberInfo;
};

const UserMetabolismCard: React.FC<UserMetabolismCardProps> = ({
    weightKg,
    sex,
    onWeightChange,
    onSexChange,
    isSelectedDayToday,
    latestPromille,
    rawPromille,
    peakPromille,
    breathMgL,
    soberInfo,
}) => {
    const { t } = useTranslation();

    return (
        <Card>
            <Row gutter={[16, 16]} align="middle">
                <Col xs={24} md={12}>
                    <Space direction="vertical">
                        <Text strong>{t("drinkStats.metabolism.weightLabel")}</Text>
                        <InputNumber
                            aria-label={t("drinkStats.metabolism.weightLabel")}
                            min={30}
                            max={250}
                            value={weightKg}
                            addonAfter="kg"
                            onChange={(value) => {
                                if (value && value > 0) {
                                    onWeightChange(value);
                                }
                            }}
                        />
                        <Text type="secondary">{t("drinkStats.metabolism.weightHelp")}</Text>
                        <Text strong>{t("drinkStats.metabolism.sexLabel")}</Text>
                        <Radio.Group
                            value={sex}
                            onChange={(event) => onSexChange(event.target.value)}
                            optionType="button"
                            buttonStyle="solid"
                        >
                            <Radio.Button value="male">{t("drinkStats.metabolism.male")}</Radio.Button>
                            <Radio.Button value="female">{t("drinkStats.metabolism.female")}</Radio.Button>
                        </Radio.Group>
                    </Space>
                </Col>
                <Col xs={24} md={12}>
                    <Statistic
                        title={
                            isSelectedDayToday
                                ? t("drinkStats.metabolism.promilleNow")
                                : t("drinkStats.metabolism.promilleSelectedDay")
                        }
                        value={latestPromille}
                        precision={3}
                        suffix="‰"
                    />
                    <Text type="secondary" style={{ display: "block", marginTop: 8 }}>
                        {t("drinkStats.metabolism.breathalyzer", {
                            mg: breathMgL.toFixed(3),
                            promille: rawPromille.toFixed(3),
                        })}
                    </Text>
                    {(rawPromille > IMPLAUSIBLE_PROMILLE_THRESHOLD || peakPromille > IMPLAUSIBLE_PROMILLE_THRESHOLD) && <Alert
                        type="warning"
                        showIcon
                        style={{ marginTop: 8 }}
                        message={t("drinkStats.metabolism.implausibleEstimate")}
                    />}
                    {soberInfo.actual && (
                        <Text type="secondary" style={{ display: "block" }}>
                            {t("drinkStats.metabolism.soberSince", {
                                time: dayjs(soberInfo.actual).format("DD MMM YYYY HH:mm"),
                            })}
                        </Text>
                    )}
                    {!soberInfo.actual && soberInfo.projected && (
                        <Text type="secondary" style={{ display: "block" }}>
                            {t("drinkStats.metabolism.soberEta", {
                                time: dayjs(soberInfo.projected).format("DD MMM YYYY HH:mm"),
                            })}
                        </Text>
                    )}
                </Col>
            </Row>
        </Card>
    );
};

export default UserMetabolismCard;
